<template>
	<div class="tab-buttons">
		
		<div v-if="isMobile" class="inline-search-container">
			<input 
				type="text"
				v-model="inlineSearchQuery"
				@input="onInlineSearch"
				@focus="onInlineFocus"
				@blur="onInlineBlur"
				:placeholder="getPlaceholder()"
				class="inline-search-input"
			/>
			<div class="inline-search-icon">
				<svg width="20" height="20" viewBox="0 0 24 24" fill="none">
					<path d="M21 21L16.5 16.5M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z" stroke="#999" stroke-width="2" stroke-linecap="round"/>
				</svg>
			</div>
			<div v-if="showInlineResults && inlineSearchResults.length > 0" class="inline-search-results">
				<div v-for="poi in inlineSearchResults" :key="poi.id" class="inline-result-item" @click="selectInlinePOI(poi)">
					{{ poi.getName(language) }}
				</div>
			</div>
		</div>
		
		<!-- Landscape: Button opens overlay -->
		<div 
			v-else-if="isLandscape"
			class="tab-button tab-search" 
			:class="{active: showLandscapeSearch}" 
			@click="toggleLandscapeSearch"
		>
			<span class="search-text" data-translation-element="search-label">OTSING</span>
			<svg class="search-btn-icon" width="20" height="20" viewBox="0 0 24 24" fill="none">
				<path d="M21 21L16.5 16.5M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
			</svg>
		</div>
		
		<!-- Portrait: Button opens WFSearch overlay -->
		<div 
			v-else
			class="tab-button tab-search" 
			:class="{active: searchVisible}" 
			@click="switchTab('search')"
		>
			<span class="search-text" data-translation-element="search-label">OTSING</span>
			<svg class="search-btn-icon" width="20" height="20" viewBox="0 0 24 24" fill="none">
				<path d="M21 21L16.5 16.5M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
			</svg>
		</div>
	</div>
</template>

<script>
import { mapState } from 'vuex';

export default {
	name: 'TabButtons',
	data() {
		return {
			inlineSearchQuery: '',
			inlineSearchResults: [],
			showInlineResults: false,
			showLandscapeSearch: false,
			landscapeSearchQuery: '',
			landscapeSearchResults: [],
			overlayElement: null,
			windowWidth: window.innerWidth,
			keyboards: {
				et: [
					['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P', 'Ü', 'Õ'],
					['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L', 'Ö', 'Ä'],
					['Z', 'X', 'C', 'V', 'B', 'N', 'M', { key: 'backspace', label: '⌫' }],
					[{ key: 'space', label: ' ' }]
				],
				en: [
					['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'],
					['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L'],
					['Z', 'X', 'C', 'V', 'B', 'N', 'M', { key: 'backspace', label: '⌫' }],
					[{ key: 'space', label: ' ' }]
				],
				ru: [
					['Й', 'Ц', 'У', 'К', 'Е', 'Н', 'Г', 'Ш', 'Щ', 'З', 'Х', 'Ъ'],
					['Ф', 'Ы', 'В', 'А', 'П', 'Р', 'О', 'Л', 'Д', 'Ж', 'Э'],
					['Я', 'Ч', 'С', 'М', 'И', 'Т', 'Ь', 'Б', 'Ю', { key: 'backspace', label: '⌫' }],
					[{ key: 'space', label: ' ' }]
				],
				fi: [
					['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P', 'Å'],
					['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L', 'Ö', 'Ä'],
					['Z', 'X', 'C', 'V', 'B', 'N', 'M', { key: 'backspace', label: '⌫' }],
					[{ key: 'space', label: ' ' }]
				]
			}
		};
	},
	computed: {
		...mapState(['searchVisible']),
		...mapState('wf', ['language']),
		isMobile() {
			return this.windowWidth <= 480;
		},
		isLandscape() {
			return this.windowWidth >= 1281;
		},
		currentKeyboardLayout() {
			return this.keyboards[this.language] || this.keyboards['et'];
		}
	},
	watch: {
		showLandscapeSearch(visible) {
			if (visible) {
				this.createOverlay();
			} else {
				this.removeOverlay();
			}
		}
	},
	mounted() {
		window.addEventListener('resize', this.handleResize);
		document.addEventListener('click', this.handleOutsideClick);
	},
	beforeDestroy() {
		window.removeEventListener('resize', this.handleResize);
		document.removeEventListener('click', this.handleOutsideClick);
		this.removeOverlay();
	},
	methods: {
		handleResize() {
			this.windowWidth = window.innerWidth;
		},
		handleOutsideClick(e) {
			if (!this.$el.contains(e.target)) {
				this.showInlineResults = false;
			}
		},
		getPlaceholder() {
			const placeholders = { et: 'OTSING', en: 'SEARCH', ru: 'ПОИСК', fi: 'HAE' };
			return placeholders[this.language] || 'OTSING';
		},
		switchTab(tab) {
			this.$emit('switchTab', tab);
		},
		
		// Mobile search
		onInlineFocus() {
			if (this.inlineSearchResults.length > 0) {
				this.showInlineResults = true;
			}
		},
		onInlineBlur() {
			setTimeout(() => { this.showInlineResults = false; }, 200);
		},
		onInlineSearch() {
			if (!this.$wayfinder || !this.inlineSearchQuery.trim()) {
				this.inlineSearchResults = [];
				this.showInlineResults = false;
				return;
			}
			const query = this.inlineSearchQuery.toLowerCase().trim();
			this.inlineSearchResults = this.searchPOIs(query);
			this.showInlineResults = this.inlineSearchResults.length > 0;
		},
		selectInlinePOI(poi) {
			this.showInlineResults = false;
			this.inlineSearchQuery = '';
			this.inlineSearchResults = [];
			this.selectPOI(poi);
		},
		
		// Landscape overlay
		toggleLandscapeSearch() {
			this.showLandscapeSearch = !this.showLandscapeSearch;
		},
		closeLandscapeSearch() {
			this.showLandscapeSearch = false;
		},
		createOverlay() {
			const overlay = document.createElement('div');
			overlay.className = 'landscape-search-overlay';
			overlay.innerHTML = this.getOverlayHTML();
			document.body.appendChild(overlay);
			this.overlayElement = overlay;
			this.bindOverlayEvents();
		},
		removeOverlay() {
			if (this.overlayElement) {
				this.overlayElement.remove();
				this.overlayElement = null;
			}
			this.landscapeSearchQuery = '';
			this.landscapeSearchResults = [];
		},
		getOverlayHTML() {
			return '<button class="landscape-close-btn" data-action="close"><span class="close-x">&times;</span><span>SULGE</span></button>' +
				'<div class="landscape-search-container">' +
					'<div class="landscape-search-results"></div>' +
					'<div class="landscape-input-wrapper">' +
						'<input type="text" readonly placeholder="' + this.getPlaceholder() + '" class="landscape-search-input"/>' +
						'<div class="landscape-input-icon"><svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M21 21L16.5 16.5M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z" stroke="white" stroke-width="2" stroke-linecap="round"/></svg></div>' +
					'</div>' +
					'<div class="landscape-keyboard">' + this.getKeyboardHTML() + '</div>' +
				'</div>';
		},
		getKeyboardHTML() {
			let html = '';
			this.currentKeyboardLayout.forEach(row => {
				html += '<div class="keyboard-row">';
				row.forEach(key => {
					if (typeof key === 'object') {
						const cls = key.key === 'backspace' ? 'keyboard-key key-backspace' : 'keyboard-key key-space';
						html += '<button class="' + cls + '" data-key="' + key.key + '">' + key.label + '</button>';
					} else {
						html += '<button class="keyboard-key" data-key="' + key + '">' + key + '</button>';
					}
				});
				html += '</div>';
			});
			return html;
		},
		bindOverlayEvents() {
			if (!this.overlayElement) return;
			
			const closeBtn = this.overlayElement.querySelector('[data-action="close"]');
			if (closeBtn) {
				closeBtn.addEventListener('click', () => this.closeLandscapeSearch());
			}
			
			const keys = this.overlayElement.querySelectorAll('.keyboard-key');
			keys.forEach(key => {
				key.addEventListener('click', (e) => {
					this.onLandscapeKeyPress(e.currentTarget.dataset.key);
				});
			});
			
			const resultsContainer = this.overlayElement.querySelector('.landscape-search-results');
			if (resultsContainer) {
				resultsContainer.addEventListener('click', (e) => {
					const item = e.target.closest('.landscape-result-item');
					if (item) {
						const index = parseInt(item.dataset.poiIndex, 10);
						const poi = this.landscapeSearchResults[index];
						if (poi) this.selectLandscapePOI(poi);
					}
				});
			}
		},
		onLandscapeKeyPress(keyValue) {
			if (keyValue === 'backspace') {
				this.landscapeSearchQuery = this.landscapeSearchQuery.slice(0, -1);
			} else if (keyValue === 'space') {
				this.landscapeSearchQuery += ' ';
			} else {
				this.landscapeSearchQuery += keyValue.toLowerCase();
			}
			
			const input = this.overlayElement.querySelector('.landscape-search-input');
			if (input) input.value = this.landscapeSearchQuery;
			
			this.performLandscapeSearch();
		},
		performLandscapeSearch() {
			if (!this.$wayfinder || !this.landscapeSearchQuery.trim()) {
				this.landscapeSearchResults = [];
				this.updateResultsDisplay();
				return;
			}
			const query = this.landscapeSearchQuery.toLowerCase().trim();
			this.landscapeSearchResults = this.searchPOIs(query);
			this.updateResultsDisplay();
		},
		updateResultsDisplay() {
			if (!this.overlayElement) return;
			const container = this.overlayElement.querySelector('.landscape-search-results');
			if (!container) return;
			
			if (this.landscapeSearchResults.length > 0) {
				container.style.display = 'block';
				let html = '';
				this.landscapeSearchResults.forEach((poi, index) => {
					html += '<div class="landscape-result-item" data-poi-index="' + index + '">' + poi.getName(this.language) + '</div>';
				});
				container.innerHTML = html;
			} else {
				container.style.display = 'none';
				container.innerHTML = '';
			}
		},
		selectLandscapePOI(poi) {
			this.closeLandscapeSearch();
			this.selectPOI(poi);
		},
		
		// Shared
		searchPOIs(query) {
			const poisData = this.$wayfinder.getPOIs();
			let allPOIs = Array.isArray(poisData) ? poisData : Object.values(poisData || {});
			return allPOIs
				.filter(poi => {
					if (!poi || typeof poi.getName !== 'function') return false;
					const name = poi.getName(this.language);
					return name && name.toLowerCase().includes(query);
				})
				.slice(0, 10);
		},
		selectPOI(poi) {
			if (poi && this.$wayfinder) {
				this.$store.dispatch('setPOI', poi);
				this.$wayfinder.showPath(poi.getNode(), poi);
				this.$wayfinder.clearHighlights();
				this.$wayfinder.setHighlights([poi]);
			}
			this.$emit('switchTab', 'poi-info');
		}
	}
};
</script>

<style scoped lang="less">
@import '../theme/variables';

.tab-buttons {
	flex: 1 1 auto;
	min-width: 0;
	height: 100%;
	display: flex;
	align-items: center;
	position: relative;
}

.tab-button {
	width: 100%;
	height: 100%;
	background-color: @main-background;
	border: 2px solid #e0e0e0;
	border-radius: 10px;
	font-weight: 600;
	font-size: 1rem;
	padding: 0 1rem;
	display: flex;
	align-items: center;
	justify-content: space-between;
	cursor: pointer;
	transition: all 0.2s ease;
	
	.search-text {
		color: #666;
	}
	
	.search-btn-icon {
		color: #999;
	}

	&:hover {
		border-color: #ccc;
		background-color: #fafafa;
	}

	&.active, &:active {
		background-color: @tertiary-color;
		border-color: #ccc;
	}
}

.inline-search-container {
	width: 100%;
	height: 100%;
	position: relative;
}

.inline-search-input {
	width: 100%;
	height: 100%;
	padding: 0.8rem 2.5rem 0.8rem 1rem;
	font-size: 1rem;
	font-weight: 600;
	border: 2px solid #e0e0e0;
	border-radius: 10px;
	background: white;
	color: #333;
	outline: none;
	transition: border-color 0.2s ease;
	
	&::placeholder {
		color: #999;
		font-weight: 500;
	}
	
	&:focus {
		border-color: #da020e;
	}
}

.inline-search-icon {
	position: absolute;
	right: 0.8rem;
	top: 50%;
	transform: translateY(-50%);
	pointer-events: none;
}

.inline-search-results {
	position: absolute;
	top: 100%;
	left: 0;
	right: 0;
	background: white;
	border: 1px solid #e0e0e0;
	border-top: none;
	border-radius: 0 0 10px 10px;
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
	z-index: 1000;
	max-height: 250px;
	overflow-y: auto;
}

.inline-result-item {
	padding: 0.9rem 1rem;
	font-size: 1rem;
	font-weight: 500;
	color: #333;
	border-bottom: 1px solid #f0f0f0;
	cursor: pointer;
	transition: background-color 0.15s ease;
	
	&:last-child {
		border-bottom: none;
	}
	
	&:active {
		background-color: #f5f5f5;
	}
}
</style>

<style lang="less">
/* Landscape overlay - non-scoped because it's appended to body */
.landscape-search-overlay {
	position: fixed;
	top: 0;
	left: 0;
	right: 420px;
	bottom: 0;
	background: rgba(200, 200, 200, 0.95);
	z-index: 9000;
	display: flex;
	flex-direction: column;
	justify-content: flex-end;
	align-items: center;
	padding-bottom: 3rem;
}

.landscape-close-btn {
	position: absolute;
	top: 1.5rem;
	right: 1.5rem;
	display: flex;
	align-items: center;
	gap: 0.5rem;
	padding: 0.8rem 1.5rem;
	background: linear-gradient(135deg, #ff4c54 0%, #dc2626 100%);
	border: none;
	border-radius: 12px;
	color: white;
	font-size: 1rem;
	font-weight: 600;
	cursor: pointer;
	box-shadow: 0 4px 12px rgba(220, 38, 38, 0.3);
	
	.close-x {
		font-size: 1.5rem;
		line-height: 1;
	}
	
	&:hover {
		transform: scale(1.05);
	}
	
	&:active {
		transform: scale(0.98);
	}
}

.landscape-search-container {
	width: 90%;
	max-width: 700px;
	display: flex;
	flex-direction: column;
	position: relative;
}

.landscape-search-results {
	position: absolute;
	bottom: 100%;
	left: 0;
	right: 0;
	margin-bottom: 0.5rem;
	background: white;
	border-radius: 16px;
	max-height: 300px;
	overflow-y: auto;
	box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
	display: none;
}

.landscape-result-item {
	padding: 1rem 1.5rem;
	font-size: 1.1rem;
	font-weight: 500;
	color: #333;
	border-bottom: 1px solid #eee;
	cursor: pointer;
	
	&:last-child {
		border-bottom: none;
	}
	
	&:hover {
		background: #f5f5f5;
	}
}

.landscape-input-wrapper {
	position: relative;
	margin-bottom: 1rem;
}

.landscape-search-input {
	width: 100%;
	padding: 1rem 4rem 1rem 1.5rem;
	font-size: 1.3rem;
	font-weight: 600;
	border: none;
	border-radius: 14px;
	background: #333;
	color: white;
	outline: none;
	box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
	
	&::placeholder {
		color: rgba(255, 255, 255, 0.6);
	}
}

.landscape-input-icon {
	position: absolute;
	right: 1.5rem;
	top: 50%;
	transform: translateY(-50%);
}

.landscape-keyboard {
	background: linear-gradient(180deg, #f8f9fa 0%, #e9ecef 100%);
	border-radius: 16px;
	padding: 1.2rem;
	box-shadow: 0 10px 40px rgba(0, 0, 0, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.9);
}

.keyboard-row {
	display: flex;
	justify-content: center;
	margin-bottom: 0.4rem;
	
	&:last-child {
		margin-bottom: 0;
	}
}

.keyboard-key {
	display: flex;
	align-items: center;
	justify-content: center;
	min-width: 3rem;
	height: 3rem;
	margin: 0 0.15rem;
	font-size: 1.2rem;
	font-weight: 500;
	color: #333;
	text-transform: uppercase;
	background: linear-gradient(180deg, #ffffff 0%, #f5f5f5 100%);
	border: none;
	border-radius: 8px;
	box-shadow: 0 3px 0 #d1d5db, 0 4px 6px rgba(0, 0, 0, 0.1);
	cursor: pointer;
	transition: all 0.08s ease;
	
	&:hover {
		background: linear-gradient(180deg, #ffffff 0%, #eeeeee 100%);
		transform: translateY(-1px);
	}
	
	&:active {
		transform: translateY(2px);
		box-shadow: 0 1px 0 #d1d5db;
		background: linear-gradient(180deg, #f0f0f0 0%, #e8e8e8 100%);
	}
}

.keyboard-key.key-backspace {
	min-width: 4.5rem;
	background: linear-gradient(180deg, #fef2f2 0%, #fee2e2 100%);
	color: #dc2626;
	font-size: 1.3rem;
	
	&:hover {
		background: linear-gradient(180deg, #fee2e2 0%, #fecaca 100%);
	}
}

.keyboard-key.key-space {
	min-width: 16rem;
	flex-grow: 1;
	max-width: 18rem;
}
</style>